// src/components/admin/ContentApprovalManager.jsx
import { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Video } from 'lucide-react';
import { useContentApproval } from './hooks/useContentApproval';
import { useAudioPlayer } from './hooks/useAudioPlayer';
import { MusicApprovalCard } from './components/MusicApprovalCard';
import { VideoApprovalCard } from './components/VideoApprovalCard';
import { MusicPlayer } from './components/musicPlayer';

export default function ContentApprovalManager({ db, currentUser }) {
  const [pendingMusic, setPendingMusic] = useState([]);
  const [pendingVideos, setPendingVideos] = useState([]);
  const [activeTab, setActiveTab] = useState('music');

  const { approving, handleApproveMusic, handleRejectMusic, handleApproveVideo, handleRejectVideo } = useContentApproval(db, currentUser);
  const { isPlaying, currentTime, duration, currentTrack, playTrack, pauseTrack, seekTo, stopTrack, formatTime } = useAudioPlayer();

  useEffect(() => {
    const musicRef = ref(db, 'artistContent/music');
    const musicUnsubscribe = onValue(musicRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const pending = Object.entries(data)
          .map(([key, value]) => ({ ...value, key }))
          .filter(item => item.status === 'pending');
        setPendingMusic(pending);
      }
    });

    const videoRef = ref(db, 'artistContent/videos');
    const videoUnsubscribe = onValue(videoRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const pending = Object.entries(data)
          .map(([key, value]) => ({ ...value, key }))
          .filter(item => item.status === 'pending');
        setPendingVideos(pending);
      }
    });

    return () => {
      musicUnsubscribe();
      videoUnsubscribe();
    };
  }, [db]);

  const handlePlayMusic = (url, music) => {
    if (currentTrack?.key === music.key && isPlaying) {
      pauseTrack();
    } else {
      playTrack(url, music);
    }
  };

  const handleTogglePlayPause = () => {
    if (isPlaying) {
      pauseTrack();
    } else if (currentTrack) {
      playTrack(currentTrack.url, currentTrack);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-[#2a2a33]">
        <div className="flex gap-4 mb-4">
          <button
            onClick={() => setActiveTab('music')}
            className={`pb-3 font-semibold text-sm border-b-2 transition ${
              activeTab === 'music'
                ? 'text-[#bd9f67] border-[#bd9f67]'
                : 'text-[#a6a6b5] border-transparent hover:text-white'
            }`}
          >
            <Music size={18} className="inline mr-2" />
            Music ({pendingMusic.length})
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`pb-3 font-semibold text-sm border-b-2 transition ${
              activeTab === 'videos'
                ? 'text-[#bd9f67] border-[#bd9f67]'
                : 'text-[#a6a6b5] border-transparent hover:text-white'
            }`}
          >
            <Video size={18} className="inline mr-2" />
            Videos ({pendingVideos.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'music' && (
          <motion.div
            key="music"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4 pb-24"
          >
            {pendingMusic.length === 0 ? (
              <div className="text-center py-12 text-[#7d7d8a]">
                <Music size={48} className="mx-auto mb-3 opacity-50" />
                <p>No pending music for approval</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingMusic.map(music => (
                  <MusicApprovalCard
                    key={music.key}
                    music={music}
                    approving={approving}
                    isCurrentTrack={currentTrack?.key === music.key}
                    isPlaying={isPlaying}
                    onPlay={handlePlayMusic}
                    onApprove={handleApproveMusic}
                    onReject={handleRejectMusic}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'videos' && (
          <motion.div
            key="videos"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {pendingVideos.length === 0 ? (
              <div className="text-center py-12 text-[#7d7d8a]">
                <Video size={48} className="mx-auto mb-3 opacity-50" />
                <p>No pending videos for approval</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingVideos.map(video => (
                  <VideoApprovalCard
                    key={video.key}
                    video={video}
                    approving={approving}
                    onApprove={handleApproveVideo}
                    onReject={handleRejectVideo}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Music Player */}
      <AnimatePresence>
        {currentTrack && (
          <MusicPlayer
            track={currentTrack}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            onTogglePlay={handleTogglePlayPause}
            onSeek={seekTo}
            onClose={stopTrack}
            formatTime={formatTime}
          />
        )}
      </AnimatePresence>
    </div>
  );
}